<template>
  <!--
    Componente headless — gestiona un THREE.Group de hitboxes invisibles encima
    del scene del Viewer Three.js para detectar clicks sobre entities del DXF.
    No renderiza nada visible en el DOM.
  -->
  <div style="display: none"></div>
</template>

<script>
import * as THREE from 'three'

/**
 * Overlay interactivo sobre el viewer Three.js de pictogramas.
 *
 * Por cada entity del cadJson que pase el `filter`, genera un quad invisible
 * a lo largo de cada segmento (PlaneGeometry rotado). Un raycaster sobre ese
 * group resuelve los clicks y emite `@epc-entity-click` con el handle + entity.
 *
 * Aditivo: no toca el render principal (batches del DxfViewer) ni el flow del
 * LayoutBuilder. Si no hay viewer listo o el cadJson no trae entities útiles,
 * el componente no agrega nada al scene y los clicks pasan transparentes.
 *
 * Eventos emitidos:
 *   @epc-entity-click  { handle, entity, point }   ← hit sobre una entity
 *
 * Props:
 *   viewer       Ref al componente Viewer.vue (para GetDxfViewer()).
 *   cadJson      Scene parseado (object con `entities` array u object indexed).
 *   interactive  Encender/apagar la captura de clicks sin recrear meshes.
 *   filter       Función (entity) => boolean. Por defecto: solo POLYLINE/LWPOLYLINE.
 *   hitWidth     Ancho del hitbox por segmento, en unidades del DXF (default 1.0).
 */
export default {
  name: 'DxfInteractiveOverlay',

  props: {
    viewer:      { type: Object,   default: null },
    cadJson:     { type: Object,   default: null },
    interactive: { type: Boolean,  default: true },
    filter: {
      type: Function,
      default: (e) => e && (e.type === 'POLYLINE' || e.type === 'LWPOLYLINE'),
    },
    hitWidth:    { type: Number,   default: 1.0 },
  },

  data() {
    return {
      group:          null,
      raycaster:      null,
      pointer:        null,
      pointerHandler: null,
      boundCanvas:    null,
    }
  },

  watch: {
    viewer:  { handler: '_Rebuild', immediate: false },
    cadJson: { handler: '_Rebuild', deep: false },
    filter:  { handler: '_Rebuild' },
    hitWidth:{ handler: '_Rebuild' },
  },

  mounted() {
    this.raycaster = new THREE.Raycaster()
    this.pointer   = new THREE.Vector2()
    this.$nextTick(() => this._Rebuild())
  },

  beforeDestroy() {
    this._Cleanup()
  },

  methods: {
    /**
     * Acceso público al grupo de hitboxes — para que el padre pueda hacer
     * hover/raycast sobre las polilíneas trackeables sin duplicar lógica.
     * Cada hitbox tiene en userData: { handle, entity }.
     */
    GetGroup() {
      return this.group
    },

    _GetDxf() {
      const dxf = this.viewer?.GetDxfViewer?.()
      if (!dxf?.HasRenderer?.()) return null
      return dxf
    },

    /**
     * Proyecta los vértices de una entity (coords DXF) a coordenadas de PANTALLA
     * (viewport, px) con la cámara + canvas del visor, para dibujar el contorno
     * del segmento en un overlay HTML/SVG. Público: lo llama el LayoutBuilder.
     * Devuelve [] si no hay cámara/canvas todavía.
     */
    ProjectEntityToScreen(entity) {
      const dxf    = this._GetDxf()
      const camera = dxf?.GetCamera?.()
      const canvas = dxf?.GetCanvas?.()
      const verts  = entity?.vertices
      if (!camera || !canvas || !Array.isArray(verts)) return []

      const origin = dxf.GetOrigin?.() || { x: 0, y: 0 }
      const rect   = canvas.getBoundingClientRect()
      const v      = new THREE.Vector3()
      const out    = []
      for (const p of verts) {
        if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) continue
        v.set(p.x - origin.x, p.y - origin.y, 0).project(camera)
        out.push({
          x: rect.left + (v.x + 1) / 2 * rect.width,
          y: rect.top  + (1 - v.y) / 2 * rect.height,
        })
      }
      return out
    },

    _Cleanup() {
      // Desbindear listener del canvas — usar la referencia guardada
      // (boundCanvas), no la actual del viewer. El canvas puede haber sido
      // reemplazado entre bind y cleanup; la limpieza confiable es contra
      // el canvas al que efectivamente le pegamos el listener.
      try {
        if (this.boundCanvas && this.pointerHandler) {
          this.boundCanvas.removeEventListener('pointerdown', this.pointerHandler)
        }
      } catch (e) {
        // Si el canvas ya no existe en el DOM no rompe el unmount.
      }
      // Timer de detección de doble-click pendiente.
      if (this._pendingClick) {
        clearTimeout(this._pendingClick.timer)
        this._pendingClick = null
      }
      this.boundCanvas    = null
      this.pointerHandler = null

      // Quitar group del scene y liberar geometría
      try {
        const dxf   = this._GetDxf()
        const scene = dxf?.GetScene?.()
        if (scene && this.group) {
          scene.remove(this.group)
          this._DisposeGroup(this.group)
        }
      } catch (e) {
        // Si el viewer ya fue destruido no rompe el unmount del componente.
      }
      this.group = null
    },

    _DisposeGroup(group) {
      group.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose?.()
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose?.())
          else obj.material.dispose?.()
        }
      })
    },

    _Rebuild() {
      this._Cleanup()

      const dxf = this._GetDxf()
      if (!dxf) return

      const scene  = dxf.GetScene?.()
      const origin = dxf.GetOrigin?.() || { x: 0, y: 0 }
      if (!scene) return

      this.group = new THREE.Group()
      this.group.name = 'DxfInteractiveOverlay'

      // entities puede llegar como array (formato del parser) o como object
      // indexed by id (formato que el frontend usa al guardar el scene).
      const raw = this.cadJson?.entities
      const list = !raw ? [] : (Array.isArray(raw) ? raw : Object.values(raw))

      for (const entity of list) {
        if (!this.filter(entity)) continue
        const vertices = entity.vertices
        if (!vertices || vertices.length < 2) continue

        for (let i = 0; i < vertices.length - 1; i++) {
          const a = vertices[i]
          const b = vertices[i + 1]
          const mesh = this._MakeSegmentHitbox(a, b, origin)
          if (!mesh) continue
          mesh.userData.handle = entity.handle
          mesh.userData.entity = entity
          this.group.add(mesh)
        }
      }

      scene.add(this.group)
      this._BindPointer(dxf)

      // Forzar repaint por si el viewer está estático.
      dxf.Render?.()
    },

    _MakeSegmentHitbox(a, b, origin) {
      // Guard NaN/Infinity: algunos DXF traen vértices con coords inválidas
      // (entities malformadas dentro de blocks, splines con bulge degenerado, etc).
      // Sin esto, PlaneGeometry + Mesh.position.set acaban con NaN y Three.js
      // dispara "Computed radius is NaN" al computar boundingSphere.
      if (!Number.isFinite(a?.x) || !Number.isFinite(a?.y) ||
          !Number.isFinite(b?.x) || !Number.isFinite(b?.y)) return null

      const ax = a.x - origin.x
      const ay = a.y - origin.y
      const bx = b.x - origin.x
      const by = b.y - origin.y

      const dx  = bx - ax
      const dy  = by - ay
      const len = Math.sqrt(dx * dx + dy * dy)
      // OJO: usar isFinite — Number(NaN) < 1e-9 es false y dejaría pasar el segmento.
      if (!Number.isFinite(len) || len < 1e-9) return null

      const geometry = new THREE.PlaneGeometry(len, this.hitWidth)
      const material = new THREE.MeshBasicMaterial({
        color:       0xffffff,
        transparent: true,
        opacity:     0,             // invisible al ojo, hit-testable por raycaster
        depthTest:   false,
        depthWrite:  false,
        side:        THREE.DoubleSide,
      })
      const mesh = new THREE.Mesh(geometry, material)
      mesh.position.set((ax + bx) / 2, (ay + by) / 2, 0.05)
      mesh.rotation.z = Math.atan2(dy, dx)
      mesh.renderOrder = 500
      return mesh
    },

    _BindPointer(dxf) {
      const canvas = dxf.GetCanvas?.()
      if (!canvas) return
      this.pointerHandler = (event) => this._OnPointer(event, dxf, canvas)
      canvas.addEventListener('pointerdown', this.pointerHandler)
      this.boundCanvas = canvas
    },

    _OnPointer(event, dxf, canvas) {
      if (!this.interactive || !this.group || this.group.children.length === 0) return

      const camera = dxf.GetCamera?.()
      if (!camera) return

      const rect = canvas.getBoundingClientRect()
      this.pointer.x = ((event.clientX - rect.left) / rect.width)  * 2 - 1
      this.pointer.y = -((event.clientY - rect.top)  / rect.height) * 2 + 1

      this.raycaster.setFromCamera(this.pointer, camera)
      const hits = this.raycaster.intersectObjects(this.group.children, false)
      if (hits.length === 0) return

      const hit = hits[0]
      const handle = hit.object.userData?.handle
      const payload = {
        handle,
        entity: hit.object.userData?.entity,
        point:  hit.point,
        // Coords de pantalla del click (para posicionar popovers en el visor).
        clientX: event.clientX,
        clientY: event.clientY,
      }

      // Distinción click vs doble-click sobre el MISMO handle:
      //   - 2º click < DBL_MS → `epc-entity-dblclick` (cancela el single pendiente).
      //   - 1er click → esperamos DBL_MS por si viene el segundo, para no disparar
      //     el single antes de detectar el doble (evita el "flash" del popover).
      const now = Date.now()
      const DBL_MS = 280

      if (this._pendingClick && this._pendingClick.handle === handle && (now - this._pendingClick.t) < DBL_MS) {
        clearTimeout(this._pendingClick.timer)
        this._pendingClick = null
        this.$emit('epc-entity-dblclick', payload)
        return
      }

      if (this._pendingClick) {
        clearTimeout(this._pendingClick.timer)
      }
      const timer = setTimeout(() => {
        this._pendingClick = null
        this.$emit('epc-entity-click', payload)
      }, DBL_MS)
      this._pendingClick = { handle, t: now, timer }
    },
  },
}
</script>
